import { formatDateLocal } from "@/shared/lib/date-time";
import { formatPriceLak } from "@/shared/lib/format-price";
import { RowActions } from "@/shared/ui/RowActions";
import { Badge, DataTable, type TanstackReactTable } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { EyeIcon } from "lucide-react";
import type { PaymentDTO } from "../api/client";

const STATUS_LABELS: Record<string, string> = {
  pending: "ລໍຖ້າ",
  paid: "ຊຳລະແລ້ວ",
  failed: "ລົ້ມເຫຼວ",
  partially_refunded: "ຄືນບາງສ່ວນ",
  refunded: "ຄືນເງິນ",
  cancelled: "ຍົກເລີກ",
};

const STATUS_BADGE_VARIANTS: Record<
  string,
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "muted"
  | "accent"
  | "success"
  | "warning"
  | "info"
> = {
  pending: "warning",
  paid: "success",
  failed: "destructive",
  partially_refunded: "info",
  refunded: "secondary",
  cancelled: "muted",
};

function paymentStatusBadge(status: string) {
  return (
    <Badge variant={STATUS_BADGE_VARIANTS[status] ?? "default"}>
      {STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

type PaymentsTableProps = {
  isLoading: boolean;
  data: PaymentDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onView: (row: PaymentDTO) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function PaymentsTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onView,
  onPaginationChange,
  onSortingChange,
}: PaymentsTableProps) {
  const columns: TanstackReactTable.ColumnDef<PaymentDTO>[] = [
    {
      id: "id",
      header: "ລະຫັດ",
      cell: ({ row }) => (
        <span className="font-mono text-xs">
          {row.original.id.slice(0, 8)}…
        </span>
      ),
    },
    {
      id: "orderCode",
      header: "ຄຳສັ່ງຊື້",
      cell: ({ row }) => (
        <Link
          to="/app/orders/$id"
          params={{ id: row.original.orderId }}
          className="font-mono text-primary text-sm hover:underline"
        >
          {row.original.orderCode ?? row.original.orderId}
        </Link>
      ),
    },
    {
      id: "amount",
      header: "ຍອດ",
      cell: ({ row }) => (
        <span className="font-medium">
          LAK {formatPriceLak(row.original.amount)}
        </span>
      ),
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => paymentStatusBadge(row.original.status),
    },
    {
      id: "externalId",
      header: "ລະຫັດພາຍນອກ",
      cell: ({ row }) => (
        <span className="font-mono text-muted-foreground text-xs">
          {row.original.externalId ?? "—"}
        </span>
      ),
    },
    {
      id: "paidAt",
      header: "ວັນທີຊຳລະ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.paidAt ? formatDateLocal(row.original.paidAt) : "—"}
        </span>
      ),
    },
    {
      id: "createdAt",
      header: "ສ້າງເມື່ອ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateLocal(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງ",
              icon: <EyeIcon className="h-4 w-4" />,
              onClick: () => onView(row.original),
            },
          ]}
          maxInline={1}
        />
      ),
    },
  ];

  return (
    <DataTable<PaymentDTO, unknown>
      noDataMessage="ບໍ່ພົບລາຍການຊຳລະ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(s) => {
        if (s[0]?.id === "") return;
        onSortingChange(s[0]?.id ?? "", s[0]?.desc ?? false);
      }}
    />
  );
}
